import { API, type EvalCaseResultResponse } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import type { BotPromptChannel, UserStory } from "@swivl-mono/types";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useDomain } from "../../../../Models/Domain/Domain.Context";

interface LastRunTranscriptProps {
  storyId: string;
  channel: BotPromptChannel;
  lastEval: NonNullable<UserStory["lastEval"]>[BotPromptChannel];
}

/**
 * Lazy-loaded inline transcript for the most recent (story × channel) eval run.
 *
 * Fetches `GET /evals/run/:runId` on first expand, then finds the case result
 * matching the story id + channel. Shows the abridged transcript inline plus a
 * "Open run →" affordance to jump to the full run-detail page.
 */
export function LastRunTranscript({ storyId, channel, lastEval }: LastRunTranscriptProps): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<EvalCaseResultResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { studioPath } = useDomain();

  if (!lastEval) return null;

  const handleToggle = async (): Promise<void> => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (result) return;
    setLoading(true);
    setError(null);
    try {
      const status = await API.BotEval.getRunStatus(lastEval.runId);
      const match = status.caseResults.find((r) => r.caseDefinition.id === storyId && r.channel === channel);
      if (!match) {
        setError(`No matching case in run #${lastEval.runId}`);
      } else {
        setResult(match);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load run");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded border border-grey-200">
      <button
        type="button"
        onClick={handleToggle}
        className="flex w-full items-center justify-between bg-grey-50 px-2 py-1.5 text-left text-xs hover:bg-grey-100"
      >
        <span className="text-grey-700">
          Last run #{lastEval.runId} · {channel} ·{" "}
          <span className={lastEval.passed ? "text-teal-600" : "text-red-600"}>
            {lastEval.passed ? "passed" : "failed"} ({lastEval.score}%)
          </span>
          <span className="ml-2 text-grey-400">{new Date(lastEval.ranAt).toLocaleString()}</span>
        </span>
        <span className="flex items-center gap-2">
          <span
            className="text-orange-600 hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              navigate(studioPath("testing", "evaluations", lastEval.runId));
            }}
          >
            Open run →
          </span>
          <span className="text-grey-400">{open ? "▾" : "▸"}</span>
        </span>
      </button>
      {open && (
        <div className="border-t border-grey-200 p-2">
          {loading && <div className="text-xs text-grey-500 italic">Loading transcript…</div>}
          {error && <div className="text-xs text-red-600">{error}</div>}
          {result && (
            <>
              <div className="mb-2 text-[11px] text-grey-500">
                {result.criterionResults.filter((c) => c.passed).length}/{result.criterionResults.length} criteria passed
              </div>
              <div className="max-h-64 space-y-1 overflow-y-auto rounded bg-white p-2 text-xs">
                {result.transcript.map((entry, i) => {
                  const roleColors: Record<string, string> = {
                    user: "text-blue-600",
                    assistant: "text-grey-800",
                    tool_call: "text-purple-600",
                    tool_result: "text-purple-400",
                    system: "italic text-grey-400"
                  };
                  const label =
                    entry.role === "tool_call" ? `tool → ${entry.toolName}` : entry.role === "tool_result" ? `tool ← ${entry.toolName}` : entry.role;
                  return (
                    <div key={i} className="flex gap-2">
                      <span className={filterClasses("shrink-0 font-mono text-[10px] uppercase", roleColors[entry.role] || "text-grey-500")}>
                        {label}
                      </span>
                      <span className="wrap-break-word text-grey-700">
                        {entry.content.length > 300 ? entry.content.slice(0, 300) + "…" : entry.content}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
